/*
 * Copyright (c) Arc XP 2025.
 */

/**
 * Enum for dictating which player controls and UI elements are available. Each control option is a
 * PlayerUiControl instance with name, element name, attributes, and optional condition.
 */
import { PlayerUiControl } from './player-ui-control';
import { MediaType } from './types';

export class PlayerUiOptions {
  static PlayButton = new PlayerUiControl('PlayButton', 'media-play-button', {});
  static TimeDisplay = new PlayerUiControl('TimeDisplay', 'media-time-display', {
    showduration: '',
  });
  static CaptionsButton = new PlayerUiControl('CaptionsButton', 'media-captions-button', {});
  static TimeRange = new PlayerUiControl('TimeRange', 'media-time-range', {});
  static PlaybackRate = new PlayerUiControl('PlaybackRate', 'media-playback-rate-button', {});
  static MuteButton = new PlayerUiControl('MuteButton', 'media-mute-button', {});
  static VolumeRange = new PlayerUiControl('VolumeRange', 'media-volume-range', {});
  static FullScreen = new PlayerUiControl('FullScreen', 'media-fullscreen-button', {});
  static LiveIndicator = new PlayerUiControl('LiveIndicator', 'media-live-button', {});
  static SeekForward = new PlayerUiControl('SeekForward', 'media-seek-forward-button', {
    seekoffset: '10',
  });
  static SeekBackward = new PlayerUiControl('SeekBackward', 'media-seek-backward-button', {
    seekoffset: '5',
  });

  /**
   * Get default controls based on media type
   *
   * @param type - The media type ('video' or 'audio')
   * @param live - Whether the player is in live mode
   * @param allowSeeking - Whether seeking is allowed
   * @param captions - Whether captions are enabled
   * @returns Array of PlayerUiControl instances
   * @throws Error if type is neither 'video' nor 'audio'
   */
  static getDefaultControls(
    type: MediaType = 'video',
    live: boolean = false,
    captions: boolean = false,
    allowSeeking: boolean = true
  ): PlayerUiControl[] {
    if (type !== 'video' && type !== 'audio') {
      throw new Error(`Invalid media type: ${type}. Expected 'video' or 'audio'.`);
    }

    const controlSet: PlayerUiControl[] = [];

    if (allowSeeking) {
      controlSet.push(this.SeekBackward, this.PlayButton, this.SeekForward);
    } else {
      controlSet.push(this.PlayButton);
    }

    controlSet.push(...this.getVolumeControls());
    if (live) {
      controlSet.push(...this.getLiveControls());
    } else {
      controlSet.push(this.TimeDisplay, this.TimeRange, this.PlaybackRate); // the standard on-demand controls
    }

    if (type === 'video' && !live) {
      if (captions) {
        controlSet.push(this.CaptionsButton);
      }
      controlSet.push(this.FullScreen);
    }

    return controlSet;
  }

  private static getVolumeControls(): PlayerUiControl[] {
    return [this.VolumeRange, this.MuteButton];
  }

  private static getLiveControls(): PlayerUiControl[] {
    return [this.LiveIndicator];
  }
}
